import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWineBottle, faPlus, faPencil, faTrash,
  faToggleOn, faToggleOff, faXmark, faBoxOpen,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./MinibarCatalog.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const CATEGORIES = ["drinks", "water", "snacks", "other"];

interface MinibarItem {
  id: number;
  name: string;
  category: string;
  price: number;
  description?: string;
  available: boolean;
}

type EditItem = Partial<MinibarItem> & { name: string; category: string; price: number };

const CAT_COLOR: Record<string, string> = {
  drinks: "#818cf8",
  water:  "#38bdf8",
  snacks: "#fbbf24",
  other:  "#94a3b8",
};

export default function MinibarCatalog() {
  const [items, setItems]         = useState<MinibarItem[]>([]);
  const [loading, setLoading]     = useState(true);
  const [filterCat, setFilterCat] = useState("All");
  const [editing, setEditing]     = useState<EditItem | null>(null);
  const [editMode, setEditMode]   = useState<"create" | "edit">("create");
  const [error, setError]         = useState("");
  const [saving, setSaving]       = useState(false);

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/api/minibar/items`, { headers })
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setEditing({ name: "", category: "drinks", price: 0, description: "", available: true });
    setEditMode("create");
    setError("");
  };

  const openEdit = (item: MinibarItem) => {
    setEditing({ ...item });
    setEditMode("edit");
    setError("");
  };

  const handleSave = async () => {
    if (!editing?.name?.trim()) { setError("Name is required"); return; }
    setSaving(true); setError("");
    try {
      if (editMode === "create") {
        await axios.post(`${API}/api/minibar/items`, editing, { headers });
      } else {
        await axios.put(`${API}/api/minibar/items/${editing!.id}`, editing, { headers });
      }
      setEditing(null);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally { setSaving(false); }
  };

  const handleToggle = async (item: MinibarItem) => {
    await axios.put(`${API}/api/minibar/items/${item.id}`, { available: !item.available }, { headers });
    load();
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this item?")) return;
    await axios.delete(`${API}/api/minibar/items/${id}`, { headers });
    load();
  };

  const filtered = filterCat === "All" ? items : items.filter(i => i.category === filterCat);

  return (
    <DashboardLayout>
      <div className="mbc-page">

        {/* Header */}
        <div className="mbc-header">
          <div>
            <h1 className="mbc-title">
              <FontAwesomeIcon icon={faWineBottle} /> Minibar Catalog
            </h1>
            <p className="mbc-sub">Manage items available for minibar charges</p>
          </div>
          <button className="mbc-btn-add" onClick={openCreate}>
            <FontAwesomeIcon icon={faPlus} /> Add Item
          </button>
        </div>

        {/* Category filter */}
        <div className="mbc-cats">
          {["All", ...CATEGORIES].map(c => (
            <button
              key={c}
              className={`mbc-cat-btn ${filterCat === c ? "active" : ""}`}
              style={filterCat === c && c !== "All" ? { borderColor: CAT_COLOR[c], color: CAT_COLOR[c], background: `${CAT_COLOR[c]}18` } : undefined}
              onClick={() => setFilterCat(c)}
            >
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </div>

        {/* Items table */}
        {loading ? (
          <div className="mbc-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="mbc-empty">
            <FontAwesomeIcon icon={faBoxOpen} className="mbc-empty-icon" />
            <p>No items yet. Add your first minibar item.</p>
          </div>
        ) : (
          <div className="mbc-table">
            <div className="mbc-thead">
              <span>Item</span>
              <span>Category</span>
              <span>Price</span>
              <span>Available</span>
              <span></span>
            </div>
            {filtered.map(item => (
              <div key={item.id} className={`mbc-row ${!item.available ? "dim" : ""}`}>
                <div className="mbc-item-cell">
                  <span className="mbc-item-name">{item.name}</span>
                  {item.description && <span className="mbc-item-desc">{item.description}</span>}
                </div>
                <span className="mbc-cat-badge" style={{ color: CAT_COLOR[item.category] || CAT_COLOR.other, background: `${CAT_COLOR[item.category] || CAT_COLOR.other}18` }}>
                  {item.category}
                </span>
                <span className="mbc-price">EGP {Number(item.price).toLocaleString()}</span>
                <button className="mbc-toggle" onClick={() => handleToggle(item)}>
                  <FontAwesomeIcon icon={item.available ? faToggleOn : faToggleOff} style={{ color: item.available ? "#34d399" : "#475569", fontSize: 22 }} />
                </button>
                <div className="mbc-actions">
                  <button className="mbc-action-btn edit" onClick={() => openEdit(item)}>
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                  <button className="mbc-action-btn del" onClick={() => handleDelete(item.id)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Edit / Create Modal ── */}
      {editing && (
        <div className="mbc-overlay" onClick={() => setEditing(null)}>
          <div className="mbc-modal" onClick={e => e.stopPropagation()}>
            <div className="mbc-modal-header">
              <h2>{editMode === "create" ? "Add Item" : "Edit Item"}</h2>
              <button onClick={() => setEditing(null)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div className="mbc-modal-body">
              {error && <div className="mbc-modal-error">{error}</div>}

              <div className="mbc-form-group">
                <label>Name *</label>
                <input
                  value={editing.name}
                  onChange={e => setEditing(p => ({ ...p!, name: e.target.value }))}
                  placeholder="e.g. Heineken 330ml"
                />
              </div>

              <div className="mbc-form-row">
                <div className="mbc-form-group">
                  <label>Category</label>
                  <select value={editing.category} onChange={e => setEditing(p => ({ ...p!, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="mbc-form-group">
                  <label>Price (EGP)</label>
                  <input
                    type="number" min={0}
                    value={editing.price}
                    onChange={e => setEditing(p => ({ ...p!, price: Number(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="mbc-form-group">
                <label>Description (optional)</label>
                <input
                  value={editing.description || ""}
                  onChange={e => setEditing(p => ({ ...p!, description: e.target.value }))}
                  placeholder="e.g. Ice cold beer"
                />
              </div>
            </div>
            <div className="mbc-modal-footer">
              <button className="mbc-btn-ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button className="mbc-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : editMode === "create" ? "Add Item" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
